import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { scrollToId } from "../landing/Navbar";

const FLOATERS = [
  { e: "🍕", cls: "left-[8%] top-[18%]", size: "text-5xl sm:text-6xl", delay: 0 },
  { e: "🎓", cls: "right-[10%] top-[16%]", size: "text-5xl sm:text-7xl", delay: 0.8 },
  { e: "✈️", cls: "left-[14%] bottom-[22%]", size: "text-4xl sm:text-6xl", delay: 1.6 },
  { e: "🛍️", cls: "right-[12%] bottom-[26%]", size: "text-4xl sm:text-5xl", delay: 2.2 },
  { e: "💻", cls: "left-[38%] top-[8%]", size: "text-4xl sm:text-5xl", delay: 1.1 },
  { e: "🤖", cls: "right-[34%] bottom-[10%]", size: "text-5xl sm:text-6xl", delay: 0.5 },
];

const LINES = [
  { text: "Learn. Earn.", cls: "text-white" },
  {
    text: "& Build.",
    cls: "bg-gradient-to-r from-[#7C3AED] via-[#06B6D4] to-[#22C55E] bg-clip-text text-transparent",
  },
];

export const BiteHero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y1 = useTransform(scrollYProgress, [0, 1], [0, 200]);
  const y2 = useTransform(scrollYProgress, [0, 1], [0, -140]);

  return (
    <section
      id="top"
      ref={ref}
      data-testid="bite-hero"
      className="relative flex min-h-screen flex-col items-center justify-center overflow-hidden px-4 pb-24 pt-32 text-center"
    >
      <motion.div style={{ y: y1 }} aria-hidden="true" className="absolute -left-40 top-24 h-[460px] w-[460px] rounded-full bg-[#7C3AED]/20 blur-[140px]" />
      <motion.div style={{ y: y2 }} aria-hidden="true" className="absolute -right-32 top-64 h-[420px] w-[420px] rounded-full bg-[#06B6D4]/15 blur-[140px]" />
      <div aria-hidden="true" className="absolute bottom-10 left-1/3 h-[300px] w-[300px] rounded-full bg-[#F97316]/10 blur-[130px]" />

      {FLOATERS.map((f) => (
        <motion.span
          key={f.e}
          initial={{ opacity: 0, scale: 0, rotate: -20 }}
          animate={{ opacity: 1, scale: 1, rotate: 0 }}
          transition={{ delay: 1 + f.delay * 0.3, type: "spring", stiffness: 160, damping: 14 }}
          aria-hidden="true"
          className={`animate-floaty absolute z-0 select-none drop-shadow-[0_20px_30px_rgba(0,0,0,0.45)] ${f.cls} ${f.size}`}
          style={{ animationDelay: `${f.delay}s` }}
        >
          {f.e}
        </motion.span>
      ))}

      <motion.div
        initial={{ opacity: 0, y: 16 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.7, delay: 0.15 }}
        className="relative z-10 mb-7 inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/5 px-4 py-2 backdrop-blur-md"
      >
        <span className="text-base">🎓</span>
        <span className="font-jbmono text-[11px] uppercase tracking-[0.22em] text-slate-300">
          FUNZ · The Super App
        </span>
      </motion.div>

      <h1
        data-testid="bite-hero-heading"
        className="relative z-10 font-display text-[13vw] font-black leading-[0.95] tracking-tight sm:text-8xl"
      >
        <span className="block overflow-hidden pb-1">
          <motion.span
            className="block text-white"
            initial={{ y: "112%" }}
            animate={{ y: 0 }}
            transition={{ duration: 0.95, delay: 0.35, ease: [0.22, 1, 0.36, 1] }}
          >
            For Everything
          </motion.span>
        </span>
        <span className="block overflow-hidden pb-1">
          <motion.span
            className="block text-white"
            initial={{ y: "112%" }}
            animate={{ y: 0 }}
            transition={{ duration: 0.95, delay: 0.48, ease: [0.22, 1, 0.36, 1] }}
          >
            Gen Z Needs To
          </motion.span>
        </span>
        {LINES.map((line, i) => (
          <span key={line.text} className="block overflow-hidden pb-2">
            <motion.span
              className={`block ${line.cls}`}
              initial={{ y: "112%" }}
              animate={{ y: 0 }}
              transition={{ duration: 0.95, delay: 0.61 + i * 0.13, ease: [0.22, 1, 0.36, 1] }}
            >
              {line.text}
            </motion.span>
          </span>
        ))}
      </h1>

      <motion.p
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8, delay: 1.05 }}
        className="relative z-10 mt-6 max-w-xl text-base leading-relaxed text-slate-400 sm:text-lg"
      >
        Learn. Create. Eat. Earn. Shop. Travel. Repeat. 🚀
      </motion.p>

      <motion.div
        initial={{ opacity: 0, y: 24 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8, delay: 1.2 }}
        className="relative z-10 mt-9 flex flex-col items-center gap-4 sm:flex-row"
      >
        <button
          data-testid="bite-cta-start"
          onClick={scrollToId("#claim")}
          className="glow-btn-b rounded-full bg-gradient-to-r from-[#7C3AED] to-[#06B6D4] px-10 py-4 font-display text-sm font-bold uppercase tracking-wider text-white"
        >
          Get Started
        </button>
        <button
          data-testid="bite-cta-demo"
          onClick={scrollToId("#reels")}
          className="flex items-center gap-2 rounded-full border border-white/15 bg-white/5 px-10 py-4 font-display text-sm font-bold uppercase tracking-wider text-slate-200 backdrop-blur-md transition-colors hover:border-[#06B6D4]/60 hover:text-white"
        >
          ▶ Watch Demo
        </button>
      </motion.div>
    </section>
  );
};
